import { Link } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { Download, Eye, FolderInput, Link2, MoreVertical, Pencil, Trash2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@app/components/ui/Toast/toast'
import { appLink } from '@app/config/appLink'
import { isAdminAtom, userAtom } from '@app/hooks/useUserProfile'
import { DATADOG_ACTIONS } from '@app/app-constants'
import { logDataDogAction } from '@app/utils/logger'
import { EvidenceDocument } from '../documents.types'
import { Folder } from '../collections.types'
import { downloadDocument } from '../service/documents.service'
import { useAddDocumentToFolder, useRemoveDocumentFromFolder } from '../hooks/useFolders'

type DocumentRowActionsProps = {
  document: EvidenceDocument
  // Preserved on the link so opening a document keeps the list's filters/sort/page intact.
  search: string
  onEdit: (document: EvidenceDocument) => void
  onDelete: (document: EvidenceDocument) => void
  folders: Folder[]
  // The collection the current view is filtered/scoped to, if any. "Remove from collection" only
  // makes sense relative to a specific collection, so it's hidden entirely on the unfiltered list.
  activeCollectionId: string | null
}

export const DocumentRowActions = ({
  document,
  search,
  onEdit,
  onDelete,
  folders,
  activeCollectionId,
}: DocumentRowActionsProps) => {
  const { data: user } = useAtomValue(userAtom)
  const isAdmin = useAtomValue(isAdminAtom)
  // Owner always deletes their own; admin deletes any document. Client-side only — the
  // backend remains the actual enforcement boundary.
  const canDelete = isAdmin || document.ownerId === user?.user_uuid

  const addToFolder = useAddDocumentToFolder()
  const removeFromFolder = useRemoveDocumentFromFolder()

  const handleAddToFolder = (folderId: string) => {
    const folderName = folders.find((f) => f.id === folderId)?.name ?? ''
    addToFolder.mutate({
      folderId,
      documentId: document.id,
      documentTitle: document.title,
      folderName,
    })
  }

  const handleRemoveFromFolder = () => {
    if (!activeCollectionId) return
    const folderName = folders.find((f) => f.id === activeCollectionId)?.name ?? ''
    removeFromFolder.mutate({
      folderId: activeCollectionId,
      documentId: document.id,
      documentTitle: document.title,
      folderName,
    })
  }

  const handleShareLink = async () => {
    const url = `${window.location.origin}${appLink.documents.path}/${document.id}`
    try {
      await navigator.clipboard.writeText(url)
      toast({ description: 'Link copied to clipboard.', position: 'top-right', type: 'success' })
    } catch {
      toast({ description: url, position: 'top-right', type: 'info' })
    }
  }

  const handleDownload = () => {
    downloadDocument(document.id, document.file.originalFilename)
      .then(() =>
        logDataDogAction(DATADOG_ACTIONS.DOCUMENT_DOWNLOADED, {
          id: document.id,
          title: document.title,
          mimeType: document.file.mimeType,
        }),
      )
      .catch(() =>
        toast({
          description: 'Failed to download document.',
          position: 'top-right',
          type: 'error',
        }),
      )
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
        <Link
          to={{ pathname: `${appLink.documents.path}/${document.id}`, search }}
          title="View document"
          aria-label="View document"
        >
          <Eye className="h-4 w-4" />
        </Link>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={handleDownload}
        title="Download"
        aria-label="Download document"
      >
        <Download className="h-4 w-4" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(document)}>
            <Pencil className="h-3.5 w-3.5" />
            Edit document details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleShareLink}>
            <Link2 className="h-3.5 w-3.5" />
            Share as a link
          </DropdownMenuItem>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>
              <FolderInput className="h-3.5 w-3.5" />
              Add to collections
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              {folders.length === 0 ? (
                <DropdownMenuItem disabled>No collections yet</DropdownMenuItem>
              ) : (
                <DropdownMenuRadioGroup
                  value={document.collectionId ?? ''}
                  onValueChange={handleAddToFolder}
                >
                  {folders.map((folder) => (
                    <DropdownMenuRadioItem key={folder.id} value={folder.id}>
                      {folder.name}
                    </DropdownMenuRadioItem>
                  ))}
                </DropdownMenuRadioGroup>
              )}
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          {activeCollectionId && (
            <DropdownMenuItem onClick={handleRemoveFromFolder}>
              <X className="h-3.5 w-3.5" />
              Remove from collection
            </DropdownMenuItem>
          )}
          {canDelete && (
            <DropdownMenuItem onClick={() => onDelete(document)} className="text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
              Delete document
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
